import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@api/queries/base';
import LockIcon from '@assets/img/icons/lock.svg?react';

import { useBuyBoostX5X10Mutation } from '@/api/queries/boost';
import { playSound } from '@/common/lib/soundEffect';
import { timeStringFormat } from '@/common/lib/timer';
import { type PlantType } from '@/common/types/common';
import { ModalContext } from '@/components/Modals/Modal/config';
import { StringSmall } from '@/components/Typography/Typography';
import { MainSmButton } from '@/components/ui/Button/Button';
import { BuyForTgStarButton } from '@/components/ui/BuyForTgStarButton/BuyForTgStarButton';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { SecondaryTimer } from '@/components/ui/Timer/SecondaryTimer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

interface BoostButtonProps {
  plantId?: string;
  plantType: PlantType;
  tgemBoostX5X10?: number;
  effectMultiplier: number;
  nextUseInSeconds?: number;
}

const CryptoBoostButton: React.FC<BoostButtonProps> = ({
  plantId,
  plantType,
  effectMultiplier,
  tgemBoostX5X10,
  nextUseInSeconds = 0,
}) => {
  const boostX5X10 = useAppSelector(
    (state) => state.user.extendedInfo?.boostForStarsCost,
  );
  const { t } = useTranslation();
  const userLevel = useAppSelector((state) => state.user.user?.level || 1);
  const [onBuyBoost, { isLoading }] = useBuyBoostX5X10Mutation();
  const { secondModal } = useContext(ModalContext);
  const dispatch = useAppDispatch();

  const onBoostX5X10 = async (): Promise<void> => {
    try {
      if (userLevel < 7) return;
      playSound('buttons/boost');

      if (tgemBoostX5X10 === 0) {
        await onBuyBoost({
          plantId: plantId || '',
          minutes: effectMultiplier,
          currency: 'free',
        }).unwrap();
        dispatch(
          api.util.invalidateTags([
            plantType.toUpperCase() as Uppercase<PlantType>,
          ]),
        );
      } else {
        if (!boostX5X10) return;
        secondModal.openModal({
          renderKind: 'buyBoostX5X10',
          props: {
            plantId: plantId || '',
            minutes: effectMultiplier,
            price: boostX5X10[effectMultiplier as 5 | 10].cost.amountUsd,
            starPrice: boostX5X10[effectMultiplier as 5 | 10].cost.amountStars,
          },
        });
      }
    } catch (error) {
      console.error(error);
      showToast(t('Something went wrong, please try again'));
    }
  };

  return tgemBoostX5X10 === 0 ? (
    <MainSmButton
      inactive={userLevel < 7}
      disabled={userLevel < 7}
      isLoading={isLoading}
      onClick={onBoostX5X10}
      text={''}
    >
      {userLevel < 7 ? (
        <div className="flex items-center justify-center gap-2">
          <LockIcon className="w-[0.875rem] h-[0.875rem]" />
          <StringSmall className="font-[500]">{t('Level')} 7</StringSmall>
        </div>
      ) : (
        <StringSmall weight="bold">{t('Free')}</StringSmall>
      )}
    </MainSmButton>
  ) : nextUseInSeconds <= 0 ? (
    <BuyForTgStarButton
      discountPrice={
        boostX5X10
          ? boostX5X10[effectMultiplier as 5 | 10].cost.amountUsd
          : 0.07
      }
      wrapperClassName="w-full"
      onClick={onBoostX5X10}
    />
  ) : (
    <MainSmButton
      className="!bg-transparent !h-[32px]"
      text={''}
      inactive={true}
      disabled={true}
    >
      <StringSmall className="flex gap-[5px] !text-[0.875rem] items-center justify-center font-[500] !text-white/40">
        {t('Next in')}
        <SecondaryTimer
          className="bg-transparent p-0"
          value={timeStringFormat(nextUseInSeconds * 1000)}
          variant="boostX5X10"
        />
      </StringSmall>
    </MainSmButton>
  );
};

export default CryptoBoostButton;
